import React from 'react';

import { StageBreadCrumb } from 'components/BreadCrumbNavigation/BreadCrumbNavigation';
import { UserStageOption } from 'pages/partners/components/UsersTab/UsersTab';
import { Permissions } from 'pages/users/components/Permissions/Permissions';
import { Preferences } from 'pages/users/components/Preferences/Preferences';
import { GMetrixTabs, GMetrixTabsTab } from 'components/GMetrixTabs';
import { Password } from 'pages/users/components/Password/Password';
import { Details } from 'pages/users/components/Details/Details';
import { useUserDetails, useUsers } from 'pages/users/context';
import { type UserPermissions } from 'pages/users/types';
import { useStickyTabIndex, useSuperState } from 'hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PartnerPermissions } from 'pages/partners';
import { User } from 'apis/UsersApi';

interface UserProfileProps {
    breadCrumbs?: StageBreadCrumb<UserStageOption>[]
    currentUserIndex: number;
    getAllUsers: (partnerId: number) => void;
    handleBreadCrumbClick?: (currentBreadCrumb: StageBreadCrumb<UserStageOption>) => void
    hidePermissionsTab?: boolean;
    partnerId?: number;
    permissions: UserPermissions | PartnerPermissions;
    setUsers: React.Dispatch<React.SetStateAction<User[]>>;
    user: User;
    users: User[];
}

export function UserProfile(props: UserProfileProps) {

    const { breadCrumbs, currentUserIndex, getAllUsers, handleBreadCrumbClick, hidePermissionsTab = false, partnerId,
        permissions, setUsers, user, users } = props;

    const [state, setState] = useSuperState({
        userDetails: user,
        userDetailsOriginal: user
    });

    const { userDetails, userDetailsOriginal } = state;

    const { formatMessage } = useIntl();
    const { createToast } = useNotification();
    const { openTabIndex, pageContext, setSubTabIndex, setTitle, subTabIndex } = useUserDetails();
    const { currentPartnerId } = useUsers();

    // Remember the active sub-tab per user across refreshes.
    useStickyTabIndex({
        entityId: pageContext?.Id,
        onRestore: index => {

            const copyOfSubTabIndex = [...subTabIndex];
            copyOfSubTabIndex[openTabIndex - 1] = index;
            setSubTabIndex(copyOfSubTabIndex);

        },
        scope: 'user',
        value: subTabIndex[openTabIndex - 1] ?? 0
    });

    const handleUserUpdate = (userDetails: User) => {

        if (userDetails) {

            const copyOfUsers = [...users];
            copyOfUsers[currentUserIndex] = userDetails;
            setUsers(copyOfUsers);

        }
        if (pageContext) {

            setTitle(`${userDetails.FirstName} ${userDetails.LastName}`, false);

        }
        getAllUsers(currentPartnerId);
        createToast(formatMessage({ id: 'account_name_has_been_updated.cf8a163f', defaultMessage: 'Account {name} has been updated.' }, { name: `${userDetails?.FirstName} ${userDetails?.LastName}` }), 'success');

    };

    return (
        <>
            <GMetrixTabs
                onSelect={i => {

                    const copyOfSubTabIndex = [...subTabIndex];
                    copyOfSubTabIndex[openTabIndex - 1] = i;
                    setSubTabIndex(copyOfSubTabIndex);

                }}
                selected={subTabIndex[openTabIndex - 1]}
            >
                <GMetrixTabsTab
                    title={(
                        <div className="joyride-details-tab">
                            <FormattedMessage defaultMessage="General Info" id="general_info.d56ce80b" />
                        </div>
                    )}
                >
                    <Details
                        getAllUsers={getAllUsers}
                        handleUserUpdate={handleUserUpdate}
                        permissions={permissions}
                        setUserDetails={val => setState({ userDetails: val })}
                        setUserDetailsOriginal={val => setState({ userDetailsOriginal: val })}
                        userDetails={userDetails}
                        userDetailsOriginal={userDetailsOriginal}
                    />
                </GMetrixTabsTab>
                {!hidePermissionsTab && (
                <GMetrixTabsTab
                    title={(
                        <div className="joyride-licenses-tab">
                            <FormattedMessage defaultMessage="Permissions" id="permissions.076a2862" />
                        </div>
                    )}
                >
                    <Permissions handleUserUpdate={handleUserUpdate} permissions={permissions} userDetails={userDetails} />
                </GMetrixTabsTab>
                )}
                <GMetrixTabsTab
                    title={(
                        <div className="joyride-licenses-tab">
                            <FormattedMessage defaultMessage="Password" description="Formulario" id="password.eb68d5ea" />
                        </div>
                    )}
                >
                    <Password
                        getAllUsers={getAllUsers}
                        handleUserUpdate={handleUserUpdate}
                        permissions={permissions}
                        setUserDetails={val => setState({ userDetails: val })}
                        setUserDetailsOriginal={val => setState({ userDetailsOriginal: val })}
                        userDetails={userDetails}
                        userDetailsOriginal={userDetailsOriginal}
                    />
                </GMetrixTabsTab>
                <GMetrixTabsTab
                    title={(
                        <div className="joyride-licenses-tab">
                            <FormattedMessage defaultMessage="Preferences" id="preferences.2bb53b2b" />
                        </div>
                    )}
                >
                    <Preferences
                        getAllUsers={getAllUsers}
                        handleUserUpdate={handleUserUpdate}
                        permissions={permissions}
                        setUserDetails={val => setState({ userDetails: val })}
                        setUserDetailsOriginal={val => setState({ userDetailsOriginal: val })}
                        userDetails={userDetails}
                        userDetailsOriginal={userDetailsOriginal}
                    />
                </GMetrixTabsTab>
            </GMetrixTabs>
        </>
    );

}
